import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Grid, Typography, Paper, Avatar, Rating, LinearProgress,
  CircularProgress, IconButton, Divider
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import StarIcon from '@mui/icons-material/Star';
import LocalPhoneOutlinedIcon from '@mui/icons-material/LocalPhoneOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import moment from 'moment';
import 'moment/locale/es';
import { getUserbyId } from '../../actions/users';
import { getReputacion } from '../../actions/profile';
import { getClientReviews } from '../../actions/reviews';
import { urlImg } from '../../components/common/helpers';

moment.locale('es');

const resolveAvatar = (u) => {
  if (!u?.avatar) return null;
  const a = u.avatar.toString();
  if (a.startsWith('http')) return a;
  if (a.startsWith('//')) return a.replace(/^\/\//, 'https://');
  return `${urlImg}/users/${u._id}/${a}?v=${u.versionAvatar || u.version || 0}`;
};

const ReviewItem = ({ review }) => {
  const autor = review.proveedor || {};
  const nombre = autor.name ? `${autor.name} ${autor.apellido || ''}`.trim() : 'Proveedor';
  const avatarSrc = resolveAvatar(autor);

  return (
    <Paper elevation={0} sx={{ p: 2.5, mb: 2, borderRadius: '16px', bgcolor: 'white', border: 1, borderColor: 'sutil' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
          <Avatar src={avatarSrc} sx={{ width: 36, height: 36, bgcolor: 'primary.main', fontSize: '0.85rem' }}>
            {!avatarSrc && nombre.charAt(0)}
          </Avatar>
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'primary.main' }}>{nombre}</Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
              {moment(review.createdAt).format('MMMM YYYY')}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Rating value={review.rating} readOnly size="small" sx={{ color: 'primary.main' }} />
          <Typography variant="caption" sx={{ fontWeight: 700, color: 'primary.main' }}>{review.rating}</Typography>
        </Box>
      </Box>
      {review.comentario && (
        <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
          "{review.comentario}"
        </Typography>
      )}
    </Paper>
  );
};

const ClientProfile = () => {
  const { idCliente } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [data, setData] = useState(null);
  const [reputacion, setReputacion] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!idCliente) return;
    setLoading(true);
    Promise.all([getUserbyId(idCliente), getClientReviews(idCliente), getReputacion(idCliente)])
      .then(([userData, reviewsData, reputacionData]) => {
        setUser(userData);
        setData(reviewsData);
        setReputacion(reputacionData);
      })
      .finally(() => setLoading(false));
  }, [idCliente]);

  if (loading) {
    return (
      <Box className="main-page" sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
        <CircularProgress sx={{ color: 'secondary.main' }} />
      </Box>
    );
  }

  const nombre = user ? `${user.name || ''} ${user.apellido || ''}`.trim() : 'Usuario';
  const primerNombre = user?.name || 'el usuario';
  const avatarSrc = resolveAvatar(user);
  const reviews = data?.reviews || [];
  const promedio = data?.promedio || 0;
  const total = data?.total || 0;
  const distribucion = data?.distribucion || { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
  const serviciosContratados = data?.serviciosContratados || 0;
  // El backend oculta correo/celular según la preferencia de privacidad del usuario.
  const contactoOculto = user?.contactoVisible === false;

  return (
    <Box className="main-page" sx={{ mb: 8 }}>
      {/* Botón atrás */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <IconButton
          onClick={() => navigate(-1)}
          sx={{ bgcolor: (t) => alpha(t.palette.secondary.main, 0.15), mr: 1, '&:hover': { bgcolor: (t) => alpha(t.palette.secondary.main, 0.3) }, width: 32, height: 32, borderRadius: '8px' }}
          size="small"
        >
          <ArrowBackIosNewIcon sx={{ fontSize: 14, color: 'primary.main' }} />
        </IconButton>
        <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>Atrás</Typography>
      </Box>

      {/* Header */}
      <Box sx={{ bgcolor: 'secondary.main', color: 'primary.main', borderRadius: 2, px: 4, py: 5, mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>Perfil del usuario</Typography>
        <Typography mt={2} variant="body1" color="primary">
          <small>Conoce el perfil de {primerNombre}</small>
        </Typography>
      </Box>

      <Grid container spacing={4}>
        {/* Columna izquierda */}
        <Grid size={{ xs: 12, md: 4 }}>
          {/* Tarjeta de foto */}
          <Paper elevation={0} sx={{ borderRadius: '16px', overflow: 'hidden', border: 1, borderColor: 'sutil', mb: 3 }}>
            <Box sx={{ position: 'relative', height: 380, bgcolor: (t) => alpha(t.palette.secondary.main, 0.15) }}>
              {avatarSrc ? (
                <Box component="img" src={avatarSrc} alt={nombre}
                  sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Avatar sx={{ width: 120, height: 120, bgcolor: 'primary.main', fontSize: '3rem' }}>
                    {nombre.charAt(0)}
                  </Avatar>
                </Box>
              )}
              <Box sx={{
                position: 'absolute', bottom: 0, left: 0, right: 0,
                background: 'linear-gradient(to top, rgba(255,255,255,0.95), rgba(255,255,255,0))',
                pt: 6, pb: 2, textAlign: 'center'
              }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>{nombre}</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mt: 0.5 }}>
                  <StarIcon sx={{ color: 'primary.main', fontSize: 18 }} />
                  <Typography variant="body2" sx={{ fontWeight: 700, color: 'primary.main' }}>
                    {promedio.toFixed(1)}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Paper>

          {/* Experiencia One Service */}
          <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main', mb: 0.5 }}>
            Experiencia One Service
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
            Experiencia de {primerNombre} como cliente
          </Typography>

          <Paper elevation={0} sx={{ p: 3, borderRadius: '16px', border: 1, borderColor: 'sutil', mb: 3 }}>
            <Grid container spacing={2}>
              <Grid size={6}>
                <LocalShippingOutlinedIcon sx={{ color: 'primary.main', fontSize: 26, mb: 1 }} />
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, display: 'block', fontSize: '0.7rem' }}>
                  SERVICIOS CONTRATADOS
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary' }}>{serviciosContratados}</Typography>
              </Grid>
              <Grid size={6}>
                <VerifiedUserOutlinedIcon sx={{ color: reputacion?.identidadVerificada ? 'primary.main' : 'text.secondary', fontSize: 26, mb: 1 }} />
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, display: 'block', fontSize: '0.7rem' }}>
                  IDENTIDAD
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary' }}>
                  {reputacion ? (reputacion.identidadVerificada ? 'Verificado' : 'Sin verificar') : '—'}
                </Typography>
              </Grid>
            </Grid>
          </Paper>

          {/* Contacto (según la privacidad del usuario) */}
          <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main', mb: 0.5 }}>
            Contacto
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
            Datos de contacto de {primerNombre}
          </Typography>
          <Paper elevation={0} sx={{ p: 3, borderRadius: '16px', border: 1, borderColor: 'sutil', mb: 3 }}>
            {contactoOculto ? (
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {primerNombre} no comparte sus datos de contacto.
              </Typography>
            ) : (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                  <LocalPhoneOutlinedIcon sx={{ color: 'primary.main' }} />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, display: 'block', fontSize: '0.7rem' }}>
                      TELÉFONO
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 600 }}>
                      {user?.tel || 'No especificado'}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <EmailOutlinedIcon sx={{ color: 'primary.main' }} />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, display: 'block', fontSize: '0.7rem' }}>
                      CORREO
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 600, wordBreak: 'break-all' }}>
                      {user?.email || 'No especificado'}
                    </Typography>
                  </Box>
                </Box>
              </>
            )}
          </Paper>

          {/* Rating + distribución */}
          <Paper elevation={0} sx={{ p: 3, borderRadius: '16px', border: 1, borderColor: 'sutil', textAlign: 'center' }}>
            <Typography variant="h2" sx={{ fontWeight: 800, color: 'common.black' }}>{promedio.toFixed(1)}</Typography>
            <Rating value={promedio} precision={0.5} readOnly sx={{ color: 'primary.main', my: 1 }} />
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
              {total} {total === 1 ? 'reseña' : 'reseñas'}
            </Typography>
            <Divider sx={{ mb: 2 }} />
            {[5, 4, 3, 2, 1].map((n) => (
              <Box key={n} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                <Typography variant="caption" sx={{ color: 'text.secondary', width: 12 }}>{n}</Typography>
                <LinearProgress
                  variant="determinate"
                  value={distribucion[n] || 0}
                  sx={{
                    flex: 1, height: 6, borderRadius: 3, bgcolor: 'divider',
                    '& .MuiLinearProgress-bar': { bgcolor: 'primary.main', borderRadius: 3 }
                  }}
                />
                <Typography variant="caption" sx={{ color: 'text.secondary', width: 36, textAlign: 'right' }}>
                  {distribucion[n] || 0}%
                </Typography>
              </Box>
            ))}
          </Paper>
        </Grid>

        {/* Columna derecha */}
        <Grid size={{ xs: 12, md: 8 }}>
          {reviews.length > 0 ? (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 2 }}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>
                    Comentarios destacados
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Comentarios de proveedores sobre {primerNombre}
                  </Typography>
                </Box>
              </Box>
              {reviews.map((review) => <ReviewItem key={review._id} review={review} />)}
            </>
          ) : (
            <Paper elevation={0} sx={{
              borderRadius: '16px', border: 1, borderColor: 'sutil', p: 6, textAlign: 'center',
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 320
            }}>
              <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main', mb: 2 }}>
                {primerNombre} <br /> aún no tiene comentarios
              </Typography>
              <Typography variant="body1" sx={{ color: 'text.secondary', maxWidth: 480 }}>
                Si tienes alguna duda específica puedes contactarlo a través del chat,
                recuerda que te daremos soporte en cada paso que necesites como proveedor.
              </Typography>
            </Paper>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default ClientProfile;
